import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles, productImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import Typography from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductAddToCartButton from "@tCooneyBrothers/Overrides/Components/ProductAddToCartButton";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    settingsCollection: getSettingsCollection(state),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    recommendationId?: number;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showAvailability: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    container?: InjectableCss;
    description?: InjectableCss;
    infoContainer?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

productImageStyles.linkWrappingImage = {
    css: css`
        background: #fff;
        justify-content: center;
        padding: 10px;
        div {
            width: 100%;
        }
        p[class^="TypographyStyle"] {
            // padding: 10px 40px;
            max-height: 150px;
            overflow: hidden;
            font-size: 12px;
        }
    `,
};

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    container: {
        css: css`
            background-color: #fafafa;
            display: flex;
            flex-direction: column;
            height: 100%;
        `,
    },
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
            padding: 5px 15px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
                padding: 5px 15px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
                border: 1.5px dotted green;
                border-right: none;
                border-left: none;
                display: flex;
                flex-wrap: wrap;
                flex: 1 1 50%;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
            padding: 5px 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-bottom: 0;
                    `,
                    css`
                        margin-bottom: 0;
                    `,
                ])}
            & + button[data-test-selector="addToListLink"] {
                justify-content: flex-end;
                position: absolute;
                bottom: 38px;
                right: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            position: static;
                        `,
                        css`
                            position: static;
                        `,
                    ])}
            }
            section {
                legend {
                    font-size: 12px;
                    font-weight: 500;
                    margin-right: 5px;
                    & + div {
                        display: flex;
                    }
                }
                [data-test-selector="productPrice_unitNetPrice"] {
                    color: #2450a8;
                    font-size: 12px;
                    font-weight: 700;
                }
                [data-test-selector="productPrice_unitOfMeasureLabel"] {
                    color: #2450a8;
                    font-size: 11px;
                    font-weight: 700;
                }
            }
        `,
    },
    availabilityGridItem: {
        width: 12,
        css: css`
            padding: 5px 15px 5px 15px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            > div[class^="GridOffset"] {
                max-width: 100%;
            }
        `,
    },
    qtyGridItem: {
        width: 5,
        css: css`
            padding: 0;
            [class^="FormFieldStyle"] {
                height: 100%;
            }
            > div > label {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            font-size: 11px;
                            padding: 3px;
                        `,
                        css`
                            font-size: 11px;
                        `,
                        null,
                    ])}
            }
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                background-color: #e3e3e3;
                flex-direction: row;
                align-items: center;
                padding: 5px;
                & div input[type="number"] {
                    height: 30px;
                    border-radius: 4px;
                    text-align: center;
                    font-size: 16px;
                }
                div {
                    margin-top: 0 !important;
                }
                & label {
                    font-weight: normal;
                    font-size: 12px;
                    padding: 6px;
                    flex-basis: 85%;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            null,
                            null,
                            null,
                            css`
                                flex-basis: 60%;
                            `,
                        ])}
                }
            `,
        },
    },
    addToCartButtonGridItem: {
        width: 7,
        css: css`
            padding: 0;
            > button {
                height: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                font-size: 11px;
                                padding: 0 20px;
                                > span {
                                    font-size: 11px;
                                }
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
                padding: 0 15px;
                text-transform: uppercase;
            `,
        },
    },
    infoContainer: {
        css: css`
            // display: flex;
            align-items: flex-end;
            height: auto;
            margin-top: auto;
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    settingsCollection,
    showAvailability,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };
    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.description}>
                    {showImage && !showOverlayBadge && (
                        <ProductImage
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productImageStyles}
                        />
                    )}
                    {showImage && showOverlayBadge && overlayBadge && (
                        <StyledWrapper
                            {...styles.badgeAreaWrapper}
                            ref={badgeAreaWrapperRef}
                            data-test-selector="productCarouselBadge"
                        >
                            <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                            <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {showBrand && product.brand && (
                        <StyledWrapper {...styles.productBrandWrapper}>
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        </StyledWrapper>
                    )}
                    {showTitle && (
                        <ProductDescription
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.infoContainer}>
                    <StyledWrapper>
                        {showPartNumbers && (
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                showCustomerName={false}
                                showManufacturerItem={true}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        )}
                        {settingsCollection.productSettings.showInventoryAvailability && (
                            <GridItem {...styles.availabilityGridItem}>
                                <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                            </GridItem>
                        )}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                        {showImageBadges && imageBadges && (
                            <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                        )}
                        {showPrice && (
                            <StyledWrapper {...styles.productPriceWrapper}>
                                <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                                    {translate("U/M")}
                                </VisuallyHidden>
                                <ProductPrice showLabel={true} product={productContext} />
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        id={`${carouselId}-uom`}
                                    />
                                )}
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.productQuantityBreakPricingStyles}
                                />
                            </StyledWrapper>
                        )}
                        {showAddToList && (
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                extendedStyles={styles.productAddToListLinkStyles}
                            />
                        )}
                        {showAddToCart && (
                            <GridContainer {...styles.addToCartContainer}>
                                <GridItem {...styles.qtyGridItem}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-qty`}
                                        id={`${carouselId}-qty-label`}
                                    >
                                        {translate("QTY_quantity")}
                                    </VisuallyHidden>
                                    <ProductQuantityOrdered
                                        labelOverride="QTY"
                                        extendedStyles={styles.productQuantityOrderedStyles}
                                        id={`${carouselId}-qty`}
                                    />
                                </GridItem>
                                <GridItem {...styles.addToCartButtonGridItem}>
                                    <ProductAddToCartButton
                                        onAddToCartClick={() => trackProductRecommendation(false)}
                                        data-test-selector="addToCartBtn"
                                        extendedStyles={styles.productAddToCartButtonStyles}
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
